import { intlLocale } from "./languages";
import { activeLanguage } from "./store";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-85 S5 — money, dates, times and sorting in the florist's language
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHAT THIS REPLACES. Before S5 the kiosk formatted money five different ways,
 * and two of them were on screen at the same time:
 *
 *   eur(n)                → `€${n.toFixed(2)}`     symbol first, dot, no locale
 *   euro(v)               → Intl, locale `undefined`, so the DEVICE's language
 *   `€${item.price}`      → the server's decimal string, unformatted
 *   ` · ${order.total} €` → symbol last, also unformatted
 *   a fifth copy in ShopFloorSaleCourierBlock
 *
 * Which is how the owner came to see `не менее 305.00 €` on a Russian screen
 * (2026-09-21): Russian text, a dot for a decimal separator, and a total
 * beside it written `€10.00`. One rule, one home.
 *
 * READS THE LANGUAGE AT CALL TIME, like the module `t`. That means a component
 * showing money must be SUBSCRIBED — `useT()` — or it will keep the old
 * language's formatting until it re-renders for some other reason. Same
 * trade-off as `fulfillmentLabel`, and the same answer: every caller here is
 * already a component that calls `useT`.
 *
 * TWO OWNER RULINGS ARE HARDCODED HERE, both from 2026-09-21:
 *
 *   € ALWAYS, never the code. Ukrainian's default for EUR is `1 234,50 EUR`,
 *   which reads as a foreign price rather than the one on the shelf. Owner:
 *   *"of course consistency is important use '€'"*. `narrowSymbol` is what
 *   forces it. The SYMBOL is fixed; its POSITION is not — Russian writes
 *   `10,00 €` and English `€10.00`, and that is correct in each.
 *
 *   24-HOUR, ALWAYS. Owner: *"time always in 24 hour format: 17:30"*. The
 *   CRM was rendering `01:41 AM` on the order timeline. `hourCycle: "h23"`
 *   does it — and `hour12` is deliberately never passed alongside, because
 *   setting both is how you get the one you did not want.
 *
 * NOT IN SCOPE HERE: the printed receipt stays `es-ES` (its own ruling), and
 * `localDayKey` stays locale-free because it is a KEY, not a date on screen.
 */

function locale(): string {
  return intlLocale(activeLanguage());
}

/**
 * `narrowSymbol` is ICU-dependent and throws `RangeError` where it is missing
 * rather than degrading. The fallback keeps a price on screen — `EUR 10,00`
 * beats a blank card or a crash — and this is the only place that has to know.
 */
function currencyFormat(): Intl.NumberFormat {
  const base: Intl.NumberFormatOptions = {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  try {
    return new Intl.NumberFormat(locale(), {
      ...base,
      currencyDisplay: "narrowSymbol",
    });
  } catch {
    return new Intl.NumberFormat(locale(), base);
  }
}

/** What a missing amount shows. An em dash, not "0,00 €", which is a lie. */
export const NO_AMOUNT = "—";

/**
 * An amount the server sent — a number, or Prisma's `Decimal` as a string —
 * as a number, or null when there is none. The one parser `money`, `cost` and
 * the kiosk's cost rule (`kioskCostDisplay.ts`) share: never `Number(null)`
 * or `Number("")`, which are 0 (WO-B, OD-198).
 */
export function amountOf(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Money, from a number or from the server's decimal STRING.
 *
 * Prisma serialises `Decimal` as a string, and most call sites pass it
 * straight through — which is why this takes both. A value that is not a
 * number at all returns the dash rather than `NaN €`.
 */
export function money(value: number | string | null | undefined): string {
  const n = amountOf(value);
  if (n == null) return NO_AMOUNT;
  return currencyFormat().format(n);
}

/**
 * A per-unit COST, which is not the same shape of number as a price.
 *
 * `Item.defaultPurchaseUnitCostEur` and `InventoryBatch.purchaseUnitCostEur`
 * are `Decimal(12, 4)` in the schema, where sale prices are `Decimal(12, 2)`.
 * Paper and ribbon are bought by the centimetre, so a real cost looks like
 * 0.0375 per cm — and `money()` rounds that to 0.04, or 0.0049 to 0.00,
 * showing a cost the shop really pays as free. Found reviewing S5b, which had
 * routed both cost sites through `money()` along with the prices.
 *
 * Two decimals minimum so 1.20 does not shrink to 1.2, four maximum so the
 * schema's precision survives to the screen.
 */
export function cost(value: number | string | null | undefined): string {
  const n = amountOf(value);
  if (n == null) return NO_AMOUNT;
  const base: Intl.NumberFormatOptions = {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  };
  try {
    return new Intl.NumberFormat(locale(), {
      ...base,
      currencyDisplay: "narrowSymbol",
    }).format(n);
  } catch {
    return new Intl.NumberFormat(locale(), base).format(n);
  }
}

/** A date on SCREEN. For a date used as a key, see `utils/dayKey`. */
export function dateText(
  d: Date,
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
): string {
  return new Intl.DateTimeFormat(locale(), options).format(d);
}

/**
 * A calendar day as the server sends it (`YYYY-MM-DD`, a `@db.Date`) — read in
 * UTC so the day never shifts with the browser's offset. Not a date → as is.
 */
export function calendarDayText(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  return dateText(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))), {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** A clock time, always 24-hour (owner ruling). */
export function timeText(d: Date): string {
  return new Intl.DateTimeFormat(locale(), {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

/** Date and time together, 24-hour. */
export function dateTimeText(
  d: Date,
  dateOptions: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
): string {
  return new Intl.DateTimeFormat(locale(), {
    ...dateOptions,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

/**
 * A plain number — quantities, counts, line totals.
 *
 * `n.toLocaleString()` with no argument follows the DEVICE, which is how a
 * Russian screen ends up with a thousands separator chosen by a tablet
 * configured in Spanish. The same call, told which language to use.
 */
export function numberText(n: number): string {
  return new Intl.NumberFormat(locale()).format(n);
}

/**
 * A stored `Decimal(5, 2)` percent for the screen — "21.00" → "21", "10.50"
 * → "10.5" — in the reader's number format. Not a number at all shows as it
 * came. (G-121: IVA rates and discount percentages.)
 */
export function percentText(value: string | number): string {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? numberText(n) : String(value);
}

/**
 * Sorting text the way the reader expects.
 *
 * `a.localeCompare(b)` with no locale follows the DEVICE, so a Russian list on
 * a Spanish-configured tablet sorts by Spanish rules. Cyrillic is the case
 * that shows it: `Ё` sorts beside `Е` in Russian and nowhere sensible under a
 * Latin collation.
 */
export function compareText(a: string, b: string): number {
  return a.localeCompare(b, locale());
}
